import React, { useLayoutEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { VideoBackground } from '../VideoBackground';
import './MarketingLanding.css';

const SCROLL_ATTR = 'data-marketing-scroll';

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="9" fill="rgba(34,197,94,0.15)" />
    <path d="M5 9l3 3 5-5" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function MarketingLanding() {
  const navigate = useNavigate();

  // Use layout effect to prevent a visible RTL->LTR flash on first paint
  // when the user's saved language is Arabic (global html[dir="rtl"]).
  useLayoutEffect(() => {
    const root = document.documentElement;
    const prevDir = root.getAttribute('dir');
    const prevLang = root.getAttribute('lang');
    root.setAttribute(SCROLL_ATTR, 'true');
    root.setAttribute('dir', 'ltr');
    root.setAttribute('lang', 'nl');
    return () => {
      root.removeAttribute(SCROLL_ATTR);
      if (prevDir) root.setAttribute('dir', prevDir);
      else root.removeAttribute('dir');
      if (prevLang) root.setAttribute('lang', prevLang);
      else root.removeAttribute('lang');
    };
  }, []);

  const topNavExtras = (
    <nav className="marketing-nav-anchors" aria-label="Pagina-secties">
      <a href="#hoe-het-werkt">Hoe het werkt</a>
      <Link to="/subscription" className="marketing-nav-anchors__pricing">Prijzen</Link>
    </nav>
  );

  const belowFold = (
    <div className="marketing-below-fold">

      {/* ── TRUST STRIP ── */}
      <section className="marketing-band marketing-band--strip" aria-label="Kenmerken">
        <div className="tc-container">
          <ul className="marketing-strip">
            <li>
              <CheckIcon />
              <span>Gebaseerd op CBR-vragen</span>
            </li>
            <li>
              <CheckIcon />
              <span>Gericht op jouw zwakke punten</span>
            </li>
            <li>
              <CheckIcon />
              <span>Geen tijdverspilling</span>
            </li>
          </ul>
        </div>
      </section>

      {/* ── SOLUTION SECTION ── */}
      <section className="marketing-band marketing-band--solution" aria-labelledby="solution-title">
        <div className="tc-container tc-container--narrow">
          <p className="marketing-overline">De slimmere manier</p>
          <h2 id="solution-title" className="marketing-section-title">
            Alleen oefenen wat je écht nodig hebt
          </h2>
          <p className="marketing-section-body">
            TheoryCoach kijkt waar jij fouten maakt en bouwt daar je volgende oefensessie omheen.
            Geen verspilde tijd op makkelijke vragen. Alleen focus op wat jou dichter bij slagen brengt.
          </p>
          <div className="marketing-solution-grid">
            <div className="marketing-solution-item">
              <div className="marketing-solution-icon">⚡</div>
              <h3>Sneller klaar</h3>
              <p>Gemiddeld klaar in 1–2 weken in plaats van maanden van eindeloos oefenen.</p>
            </div>
            <div className="marketing-solution-item">
              <div className="marketing-solution-icon">🎯</div>
              <h3>Raak, niet breed</h3>
              <p>Focust op jouw zwakste onderwerpen. Geen overbodige herhaling.</p>
            </div>
            <div className="marketing-solution-item">
              <div className="marketing-solution-icon">📊</div>
              <h3>Je ziet je groei</h3>
              <p>Na elk examen zie je precies hoeveel je verbeterd bent en wat er nog rest.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="hoe-het-werkt" className="marketing-band marketing-band--alt" aria-labelledby="steps-title">
        <div className="tc-container">
          <p className="marketing-overline marketing-overline--center">In drie stappen naar slagen</p>
          <h2 id="steps-title" className="marketing-section-title marketing-section-title--center">
            Hoe het werkt
          </h2>
          <ol className="marketing-steps">
            <li className="tc-surface marketing-step">
              <span className="marketing-step__num">1</span>
              <h3 className="marketing-step__title">Oefen gericht</h3>
              <p className="marketing-step__text">
                Maak oefenexamens die zijn opgebouwd uit echte CBR-vragen. Precies zoals op het echte examen.
              </p>
            </li>
            <li className="tc-surface marketing-step">
              <span className="marketing-step__num">2</span>
              <h3 className="marketing-step__title">Verspil geen tijd</h3>
              <p className="marketing-step__text">
                Na elk examen ziet TheoryCoach waar jij struikelt. Je volgende sessie gaat dáár op in — niet op wat je al kunt.
              </p>
            </li>
            <li className="tc-surface marketing-step">
              <span className="marketing-step__num">3</span>
              <h3 className="marketing-step__title">Slaag-gericht oefenen</h3>
              <p className="marketing-step__text">
                Een persoonlijk oefenexamen, opgebouwd rond jouw zwakkere onderdelen. Geen generieke quiz, maar training die werkt.
              </p>
            </li>
          </ol>
        </div>
      </section>

      {/* ── PRODUCT PREVIEW ── */}
      <section className="marketing-band marketing-band--preview" aria-labelledby="preview-title">
        <div className="tc-container">
          <p className="marketing-overline marketing-overline--center">Zo ziet het eruit</p>
          <h2 id="preview-title" className="marketing-section-title marketing-section-title--center">
            Precies zoals op het CBR-examen
          </h2>
          <div className="marketing-preview">
            <div className="marketing-preview__device">
              <div className="marketing-preview__chrome">
                <div className="marketing-preview__chrome-dot" />
                <div className="marketing-preview__chrome-dot" />
                <div className="marketing-preview__chrome-dot" />
              </div>
              <div className="marketing-example__visual tc-surface" style={{ borderRadius: '0 0 12px 12px', margin: 0 }}>
                <div className="marketing-example__image">
                  <img
                    src="https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/roundabout-rules/q8-pedestrian-exit.jpg"
                    alt="Voorbeeld theorie-vraag over een rotonde met voetganger"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="marketing-example__overlay">
                  <div className="marketing-example__q">
                    Je verlaat een rotonde. Er staat een voetganger bij het zebrapad op de uitrit. Wat moet je doen?
                  </div>
                  <div className="marketing-example__answers" aria-hidden="true">
                    <div className="marketing-example__answer marketing-example__answer--wrong">
                      <span className="marketing-example__letter">A</span>
                      <span>Doorrijden, jij hebt voorrang</span>
                    </div>
                    <div className="marketing-example__answer marketing-example__answer--correct">
                      <span className="marketing-example__letter">B</span>
                      <span>Stoppen en de voetganger voor laten gaan</span>
                    </div>
                  </div>
                  <div className="marketing-example__explain">
                    <div className="marketing-example__explainTitle">Uitleg</div>
                    <div className="marketing-example__explainText">
                      Bij een zebrapad heeft de voetganger voorrang. Je remt af en stopt als dat nodig is.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="marketing-preview__copy">
              <ul className="marketing-preview__bullets">
                <li>
                  <CheckIcon />
                  <span>Foute antwoorden direct zichtbaar</span>
                </li>
                <li>
                  <CheckIcon />
                  <span>Uitleg die je onthoudt, geen lange teksten</span>
                </li>
                <li>
                  <CheckIcon />
                  <span>Vragen uit de echte CBR-database</span>
                </li>
                <li>
                  <CheckIcon />
                  <span>Training op jouw zwakke punten</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="marketing-band marketing-band--alt" aria-labelledby="faq-title">
        <div className="tc-container tc-container--narrow">
          <h2 id="faq-title" className="marketing-section-title marketing-section-title--center">
            Veelgestelde vragen
          </h2>
          <div className="marketing-faq">
            <details className="marketing-faq__item">
              <summary>Wat is TheoryCoach?</summary>
              <p>
                TheoryCoach is een slimme oefencoach voor je Nederlandse rijtheorie-examen. Je oefent per onderwerp,
                ziet waar je nog fouten maakt, en traint met een persoonlijk oefenexamen dat meebeweegt met jouw
                zwakkere punten — gericht leren, geen tijdverspilling.
              </p>
            </details>
            <details className="marketing-faq__item">
              <summary>Is het gratis?</summary>
              <p>
                Je kunt gratis beginnen met 3 oefenexamens en 1 persoonlijk examen. Wil je daarna onbeperkt oefenen
                en verder werken aan je zwakke punten? Dan kies je een abonnement — al vanaf €8 per week.
              </p>
            </details>
            <details className="marketing-faq__item">
              <summary>Werkt het op mijn telefoon?</summary>
              <p>Ja. TheoryCoach werkt op zowel mobiel als desktop — waar het voor jou het beste uitkomt.</p>
            </details>
            <details className="marketing-faq__item">
              <summary>Welke talen kan ik kiezen?</summary>
              <p>
                Bij de start kies je een taal: Nederlands, Engels of Arabisch. De CBR-theorie blijft de basis;
                de interface helpt je in de taal die jij prettig vindt.
              </p>
            </details>
            <details className="marketing-faq__item">
              <summary>Kan ik opzeggen?</summary>
              <p>Ja, op elk moment. Je behoudt toegang tot het einde van je lopende periode. Geen gedoe.</p>
            </details>
            <details className="marketing-faq__item">
              <summary>Is dit het officiële CBR-examen?</summary>
              <p>
                Nee — TheoryCoach is een oefenomgeving ter voorbereiding. Het officiële theorie-examen doe je bij het CBR.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="marketing-final-cta" aria-labelledby="marketing-final-cta-title">
        <div className="tc-container marketing-final-cta__inner">
          <h2 id="marketing-final-cta-title" className="marketing-final-cta__title">
            Klaar om te slagen?
          </h2>
          <p className="marketing-final-cta__sub">
            Begin vandaag gratis. Geen creditcard nodig om te starten.
          </p>
          <div className="marketing-final-cta__buttons">
            <button
              type="button"
              className="auth-content-cta-primary marketing-final-cta__primary"
              onClick={() => navigate('/language')}
            >
              Start met oefenen (gratis)
            </button>
            <button
              type="button"
              className="auth-content-cta-secondary marketing-final-cta__secondary"
              onClick={() => navigate('/login')}
            >
              Ik heb al een account
            </button>
          </div>
        </div>
      </section>

      {/* ── AMSTERDAM FOOTER ── */}
      <section className="marketing-amsterdam" aria-label="Made in Amsterdam">
        <div className="tc-container marketing-amsterdam__inner">
          <div className="marketing-amsterdam__xxx" aria-hidden="true">
            <span>X</span>
            <span>X</span>
            <span>X</span>
          </div>
          <div className="marketing-amsterdam__title">
            <span className="marketing-amsterdam__madein">Made in</span>{' '}
            <span className="marketing-amsterdam__city">Amsterdam</span>
          </div>
          <div className="marketing-amsterdam__copyright">© 2026 theorycoach.com</div>
          <p className="marketing-amsterdam__sub">
            <span className="marketing-amsterdam__subLead">Secure payments by</span>
            <a
              className="marketing-amsterdam__stripeLink"
              href="https://stripe.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Stripe — opens Stripe website in a new tab"
            >
              Stripe
            </a>
          </p>
        </div>
      </section>

      <footer className="marketing-footer">
        <div className="tc-container marketing-footer__inner">
          <p className="marketing-footer__text">
            <Link to="/settings/terms">Voorwaarden</Link>
            <span aria-hidden> · </span>
            <Link to="/settings/privacy">Privacybeleid</Link>
            <span aria-hidden> · </span>
            <Link to="/cookies">Cookies</Link>
          </p>
        </div>
      </footer>
    </div>
  );

  return (
    <VideoBackground variant="scrollable" topNavExtras={topNavExtras} afterHero={belowFold} hideFooter>
      <div className="tc-stack">
        <h1 className="auth-content-headline marketing-hero__headline">
          Slaag voor je CBR theorie-examen —<br className="marketing-hero__br" />
          <span className="marketing-hero__accent">sneller en slimmer</span>
        </h1>
        <p className="marketing-hero__sub">
          Stop met eindeloos oefenen. TheoryCoach focust op jouw zwakke punten
          zodat je minder tijd verspilt en sneller slaagt.
        </p>
        <div className="auth-content-ctas">
          <button type="button" className="auth-content-cta-primary" onClick={() => navigate('/language')}>
            Start met oefenen (gratis)
          </button>
          <button type="button" className="auth-content-cta-secondary" onClick={() => navigate('/login')}>
            Ik heb al een account
          </button>
        </div>
      </div>
    </VideoBackground>
  );
}
